import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface CtaButtonProps {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  onBack?: () => void;
  alwaysActive?: boolean;
  icon?: React.ReactNode;
}

export function CtaButton({ label, onClick, disabled, onBack, alwaysActive, icon }: CtaButtonProps) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  const isActive = alwaysActive || !disabled;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
      <div style={{ minWidth: mobile ? 60 : 80 }}>
        {onBack && (
          <button type="button" onClick={onBack}
            onMouseEnter={e => { e.currentTarget.style.color = theme.colors.navy; }}
            onMouseLeave={e => { e.currentTarget.style.color = theme.colors.textMuted; }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: theme.colors.textMuted, fontSize: f.buttonFontSize,
              display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
              fontWeight: theme.fontWeight.bold,
              transition: `color ${theme.transitions.fast}`,
            }}>
            <ArrowLeft size={14} /> Back
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled && !alwaysActive}
        style={{
          padding: f.buttonPadding,
          background: isActive ? theme.colors.teal : theme.colors.borderLight,
          color: isActive ? 'white' : theme.colors.muted,
          border: 'none',
          borderRadius: theme.borderRadius.full,
          fontSize: f.buttonFontSize,
          fontWeight: theme.fontWeight.bold,
          cursor: isActive ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
          transition: 'background 0.2s, transform 0.15s, box-shadow 0.15s',
          boxShadow: isActive ? theme.shadows.button : 'none',
          transform: alwaysActive ? 'translateY(-2px)' : 'translateY(0)',
        }}
        onMouseEnter={e => { if (isActive) { e.currentTarget.style.background = theme.colors.teal; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
        onMouseLeave={e => { e.currentTarget.style.background = isActive ? theme.colors.teal : theme.colors.borderLight; e.currentTarget.style.transform = alwaysActive ? 'translateY(-2px)' : 'translateY(0)'; }}
      >
        {label} {icon ?? (!alwaysActive && <ArrowRight size={16} />)}
      </button>
    </div>
  );
}

interface SelectChipProps {
  label: string;
  icon?: string;
  active: boolean;
  onClick: () => void;
  size?: 'sm' | 'md';
}

export function SelectChip({ label, icon, active, onClick, size = 'md' }: SelectChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={active ? 'chip-pop' : ''}
      style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.xs,
        padding: size === 'sm' ? `${theme.spacing.xs} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.lg}`,
        borderRadius: theme.borderRadius.full,
        border: `2px solid ${active ? theme.colors.teal : theme.colors.border}`,
        background: active ? theme.colors.teal : 'white',
        color: active ? 'white' : theme.colors.textMuted,
        cursor: 'pointer',
        fontSize: theme.fontSize.base,
        fontWeight: active ? theme.fontWeight.semibold : theme.fontWeight.normal,
        transition: 'border-color 0.15s, background 0.15s, color 0.15s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = theme.colors.teal; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = theme.colors.border; }}
    >
      {icon && <span>{icon}</span>}{label}
    </button>
  );
}
